<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import UserAvatar from '~/components/orga/UserAvatar.vue'
import SourceButton from '~/components/ui/SourceButton.vue'
import SourceTag from '~/components/ui/SourceTag.vue'
import { setPendingLogoutToast } from '~/utils/authRedirect'

/** Valeurs alignées sur la prop `type` de `SourceTag`. */
import type { ProfileRoleBadgeItem } from '~/types/profile'

const HOVER_LEAVE_DELAY_MS = 800

const props = defineProps<{
  fullName: string
  email: string
  nni?: string
  avatarDisplayMode?: 'teams' | 'initials' | 'avatar'
  avatarConfig?: Record<string, unknown> | null
  photoUrl?: string | null
  roleBadges: ProfileRoleBadgeItem[]
  attachment: string
  firstLoginDisplay: string
  rights: string[]
  canAccessAdmin: boolean
}>()

const open = ref(false)
const profileRootRef = ref<HTMLElement | null>(null)
const profileTriggerButtonRef = ref<HTMLButtonElement | null>(null)

let hoverLeaveTimer: number | null = null

function cancelScheduledClose(): void {
  if (hoverLeaveTimer !== null) {
    window.clearTimeout(hoverLeaveTimer)
    hoverLeaveTimer = null
  }
}

function scheduleCloseFromHover(): void {
  cancelScheduledClose()
  hoverLeaveTimer = window.setTimeout(function closeAfterDelay() {
    hoverLeaveTimer = null
    open.value = false
  }, HOVER_LEAVE_DELAY_MS)
}

function onPointerEnterPopoverSurface(): void {
  cancelScheduledClose()
  open.value = true
}

function closePopover(): void {
  cancelScheduledClose()
  open.value = false
}

function focusTriggerAfterClose(): void {
  void nextTick(function focusTrigger() {
    profileTriggerButtonRef.value?.focus()
  })
}

function handleDocumentClick(event: MouseEvent): void {
  if (!open.value) {
    return
  }
  const root = profileRootRef.value
  if (!root) {
    return
  }
  const target = event.target
  if (target instanceof Node && root.contains(target)) {
    return
  }
  cancelScheduledClose()
  open.value = false
}

function handleDocumentKeydown(event: KeyboardEvent): void {
  if (!open.value) {
    return
  }
  if (event.key !== 'Escape') {
    return
  }
  event.preventDefault()
  cancelScheduledClose()
  open.value = false
  focusTriggerAfterClose()
}

watch(open, function syncDocumentListeners(isOpen: boolean) {
  if (isOpen) {
    document.addEventListener('click', handleDocumentClick, true)
    document.addEventListener('keydown', handleDocumentKeydown)
  } else {
    document.removeEventListener('click', handleDocumentClick, true)
    document.removeEventListener('keydown', handleDocumentKeydown)
  }
})

onBeforeUnmount(function cleanupPopoverListeners() {
  cancelScheduledClose()
  document.removeEventListener('click', handleDocumentClick, true)
  document.removeEventListener('keydown', handleDocumentKeydown)
})

const { logout } = useOidcAuth()

async function handleLogout(): Promise<void> {
  try {
    setPendingLogoutToast()
    closePopover()
    await logout()
  } catch (err) {
    console.error('Erreur lors de la déconnexion OIDC :', err)
  }
}

function onNavigateFromPopover(): void {
  cancelScheduledClose()
  open.value = false
}

function goParametres(): void {
  onNavigateFromPopover()
  void navigateTo({ path: '/parametres' })
}

function goAdmin(): void {
  onNavigateFromPopover()
  void navigateTo({ path: '/admin' })
}

const attachmentDisplay = computed(() => {
  const value = props.attachment.trim()
  return value.length > 0 ? value : 'Non renseigné'
})

const firstLoginDisplay = computed(() => {
  const value = props.firstLoginDisplay.trim()
  return value.length > 0 ? value : 'Non renseignée'
})

const panelId = 'profile-popover-dialog'

function onCloseButtonClick(): void {
  cancelScheduledClose()
  open.value = false
  focusTriggerAfterClose()
}
</script>

<template>
  <div
    ref="profileRootRef"
    class="profile-popover"
    :class="{ 'is-open': open }"
    @mouseenter="onPointerEnterPopoverSurface"
    @mouseleave="scheduleCloseFromHover"
  >
    <button
      ref="profileTriggerButtonRef"
      class="profile-button"
      type="button"
      aria-label="Ouvrir le profil"
      data-onboarding="profile-popover-trigger"
      :aria-expanded="open"
      :aria-controls="panelId"
    >
      <span class="avatar trigger">
        <UserAvatar
          :full-name="fullName"
          :email="email"
          :nni="nni"
          :display-mode="avatarDisplayMode"
          :avatar-config="avatarConfig"
          :photo-url="photoUrl"
        />
      </span>
    </button>

    <Transition name="profile-panel">
      <div
        v-show="open"
        :id="panelId"
        class="profile-modal"
        role="dialog"
        aria-label="Profil utilisateur"
        @mouseenter="onPointerEnterPopoverSurface"
        @mouseleave="scheduleCloseFromHover"
      >
        <div class="pm-header">
          <SourceButton
            class="pm-close"
            type="tertiary"
            size="xsmall"
            icon="close"
            aria-label="Fermer le profil"
            @click="onCloseButtonClick"
          />
          <div class="pm-id">
            <div class="pm-names">
              <p class="pm-name">
                {{ fullName }}
              </p>
              <div class="pm-role-badges">
                <SourceTag
                  v-for="(badge, index) in roleBadges"
                  :key="index"
                  :text="badge.label"
                  variant="outline"
                />
              </div>
            </div>
            <div class="avatar header">
              <UserAvatar
                variant="panel"
                :full-name="fullName"
                :email="email"
                :nni="nni"
                :display-mode="avatarDisplayMode"
                :avatar-config="avatarConfig"
                :photo-url="photoUrl"
              />
            </div>
          </div>
        </div>

        <section class="pm-block">
          <div class="pm-label">
            Mon rattachement
          </div>
          <div class="pm-value">
            {{ attachmentDisplay }}
          </div>
        </section>

        <section class="pm-block">
          <div class="pm-label">
            Date de première connexion
          </div>
          <div class="pm-value">
            {{ firstLoginDisplay }}
          </div>
        </section>

        <section class="pm-block">
          <div class="pm-label">
            Mes droits
          </div>
          <div class="pm-chips">
            <span v-for="rightLabel in rights" :key="rightLabel" class="pm-chip">{{ rightLabel }}</span>
          </div>
        </section>

        <div class="pm-actions-primary" role="group" aria-label="Actions principales">
          <div v-if="canAccessAdmin" class="pm-admin-settings-row">
            <div class="pm-admin-settings-row__admin">
              <SourceButton type="secondary" text="Administration" size="medium" @click="goAdmin" />
            </div>
            <div class="pm-admin-settings-row__settings">
                  <SourceButton
                    type="secondary"
                    icon="settings"
                    text=""
                    size="medium"
                    aria-label="Paramètres"
                    @click="goParametres"
                  />
            </div>
          </div>
          <div v-else class="pm-settings-only">
            <SourceButton type="secondary" icon="settings" text="Paramètres" size="medium" @click="goParametres" />
          </div>
        </div>

        <div class="pm-actions pm-actions--logout">
          <SourceButton
            class="pm-logout-btn"
            type="deleteSecondary"
            text="Déconnexion"
            icon="logout"
            size="medium"
            @click="handleLogout"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
@use "~/assets/scss/partials/components/profile-popover";

.pm-admin-settings-row__admin {
  :deep(.source-button) {
    width: 100%;
    justify-content: center;
  }
}

.pm-admin-settings-row__settings {
  :deep(.source-tool-hover-card-trigger) {
    display: flex;
    width: 100%;
    justify-content: stretch;
  }

  :deep(.source-button) {
    width: 100%;
    justify-content: center;
  }
}

.pm-settings-only {
  :deep(.source-button) {
    width: 100%;
    justify-content: center;
  }
}

.pm-settings-only__link {
  :deep(.source-button) {
    width: 100%;
    justify-content: center;
  }
}
</style>
